import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const PagoMovilComponent = ({ user }) => {
  const [tab, setTab] = useState('pagar');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [myPayments, setMyPayments] = useState([]);
  const [myBankData, setMyBankData] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Payment flow
  const [paymentStep, setPaymentStep] = useState(0); // 0=form, 1=bank details, 2=upload, 3=result
  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState('recharge');
  const [method, setMethod] = useState('pago_movil');
  const [currentPayment, setCurrentPayment] = useState(null);
  const [lastDigits, setLastDigits] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verifyMode, setVerifyMode] = useState('screenshot'); // 'screenshot' or 'sms'
  const [smsText, setSmsText] = useState('');
  // Bank data form
  const [bankForm, setBankForm] = useState({ bank_name: '', phone: '', cedula: '', account_type: 'corriente' });
  // Admin config
  const [adminBank, setAdminBank] = useState({ bank_name: '', account_holder: '', cedula: '', phone: '', account_type: 'corriente' });

  const isAdmin = user?.email === 'admin@cualapp.com';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, bankRes] = await Promise.all([
        axios.get(`${API}/pago-movil/my-payments`),
        axios.get(`${API}/pago-movil/my-bank-data`)
      ]);
      setMyPayments(paymentsRes.data.payments || []);
      setMyBankData(bankRes.data.bank_data);
      if (bankRes.data.bank_data) {
        setBankForm(bankRes.data.bank_data);
      }
      if (isAdmin) {
        const [pendingRes, configRes] = await Promise.all([
          axios.get(`${API}/pago-movil/admin/payments/pending`),
          axios.get(`${API}/pago-movil/admin/bank-config`)
        ]);
        setPendingPayments(pendingRes.data.payments || []);
        setBankAccounts(configRes.data.accounts || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const initiatePayment = async () => {
    if (!amount || parseFloat(amount) <= 0) { alert('Ingresa un monto valido'); return; }
    try {
      const res = await axios.post(`${API}/pago-movil/payments/initiate`, {
        amount_bs: parseFloat(amount), purpose, payment_method: method
      });
      setCurrentPayment(res.data);
      setPaymentStep(1);
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const uploadProof = async () => {
    if (!screenshot || lastDigits.length !== 4) {
      alert('Sube el comprobante e ingresa los ultimos 4 digitos de la referencia');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', screenshot);
      formData.append('last_4_digits', lastDigits);
      const res = await axios.post(
        `${API}/pago-movil/payments/${currentPayment.payment_id}/upload-proof`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setVerificationResult(res.data);
      setPaymentStep(3);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error al subir comprobante'); }
    finally { setUploading(false); }
  };

  const verifySms = async () => {
    if (!smsText || smsText.length < 10) {
      alert('Pega el texto del SMS o email de confirmacion de tu banco');
      return;
    }
    setUploading(true);
    try {
      const res = await axios.post(
        `${API}/pago-movil/payments/${currentPayment.payment_id}/verify-sms`,
        { sms_text: smsText }
      );
      setVerificationResult(res.data);
      setPaymentStep(3);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error al verificar SMS'); }
    finally { setUploading(false); }
  };

  const saveBankData = async () => {
    if (!bankForm.bank_name || !bankForm.phone || !bankForm.cedula) {
      alert('Completa todos los campos'); return;
    }
    try {
      await axios.post(`${API}/pago-movil/my-bank-data`, bankForm);
      alert('Datos bancarios guardados');
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const saveAdminBank = async () => {
    if (!adminBank.bank_name || !adminBank.cedula || !adminBank.phone) {
      alert('Completa todos los campos'); return;
    }
    try {
      await axios.post(`${API}/pago-movil/admin/bank-config`, adminBank);
      setAdminBank({ bank_name: '', account_holder: '', cedula: '', phone: '', account_type: 'corriente' });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const approvePayment = async (id) => {
    try {
      await axios.post(`${API}/pago-movil/admin/payments/${id}/approve`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const rejectPayment = async (id) => {
    if (!window.confirm('Rechazar este pago?')) return;
    try {
      await axios.post(`${API}/pago-movil/admin/payments/${id}/reject`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const resetPayment = () => {
    setPaymentStep(0); setAmount(''); setCurrentPayment(null);
    setLastDigits(''); setScreenshot(null); setVerificationResult(null);
  };

  const statusColors = {
    pending_proof: 'bg-yellow-100 text-yellow-700',
    verifying: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    review: 'bg-orange-100 text-orange-700'
  };

  const statusLabels = {
    pending_proof: 'Pendiente', verifying: 'Verificando',
    approved: 'Aprobado', rejected: 'Rechazado', review: 'En revision'
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div data-testid="pago-movil-component">
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab('pagar')} data-testid="tab-pagar"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'pagar' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          Pagar
        </button>
        <button onClick={() => setTab('historial')} data-testid="tab-historial"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'historial' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          Historial
        </button>
        <button onClick={() => setTab('mis-datos')} data-testid="tab-mis-datos"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'mis-datos' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          Mis Datos Bancarios
        </button>
        {isAdmin && (
          <>
            <button onClick={() => setTab('admin-review')} data-testid="tab-admin-review"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'admin-review' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'}`}>
              Revisar Pagos {pendingPayments.length > 0 && <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">{pendingPayments.length}</span>}
            </button>
            <button onClick={() => setTab('admin-config')} data-testid="tab-admin-config"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'admin-config' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'}`}>
              Config Bancaria
            </button>
          </>
        )}
      </div>

      {/* ===== Tab: Pagar ===== */}
      {tab === 'pagar' && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {paymentStep === 0 && (
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Nuevo Pago</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Metodo</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method-select"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  <option value="pago_movil">Pago Movil</option>
                  <option value="transferencia">Transferencia Bancaria</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto (Bs.)</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder="Ej: 5000" data-testid="payment-amount-input"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proposito</label>
                <select value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="payment-purpose-select"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  <option value="recharge">Recarga de CUALcoin</option>
                  <option value="celula">Compra de Celula</option>
                </select>
              </div>
              <button onClick={initiatePayment} data-testid="initiate-payment-btn"
                className="w-full bg-emerald-600 text-white py-2.5 rounded-lg font-medium hover:bg-emerald-500 transition-colors">
                Continuar
              </button>
            </div>
          )}

          {paymentStep === 1 && currentPayment && (
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Datos para tu {method === 'pago_movil' ? 'Pago Movil' : 'Transferencia'}</h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-2xl font-bold text-emerald-700 mb-3">Bs. {currentPayment.amount_bs?.toLocaleString('es-VE', {minimumFractionDigits: 2})}</p>
                {currentPayment.bank_accounts?.map((acc, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 mb-2 border border-emerald-100">
                    <p className="font-semibold text-gray-900">{acc.bank_name}</p>
                    <p className="text-sm text-gray-600">Titular: {acc.account_holder}</p>
                    <p className="text-sm text-gray-600">Telefono: {acc.phone}</p>
                    <p className="text-sm text-gray-600">Cedula: {acc.cedula}</p>
                    <p className="text-sm text-gray-600">Tipo: {acc.account_type}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500">Realiza el pago y luego sube el comprobante.</p>
              <div className="flex gap-2">
                <button onClick={() => setPaymentStep(0)} className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50">Atras</button>
                <button onClick={() => setPaymentStep(2)} data-testid="go-to-upload-btn"
                  className="flex-1 bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-500">Ya pague, subir comprobante</button>
              </div>
            </div>
          )}

          {paymentStep === 2 && (
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Verificacion de Pago</h3>

              <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-testid="verify-mode-toggle">
                <button onClick={() => setVerifyMode('screenshot')} data-testid="mode-screenshot"
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${verifyMode === 'screenshot' ? 'bg-white shadow text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}>
                  Screenshot
                </button>
                <button onClick={() => setVerifyMode('sms')} data-testid="mode-sms"
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${verifyMode === 'sms' ? 'bg-white shadow text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}>
                  Pegar SMS / Email
                </button>
              </div>

              {verifyMode === 'screenshot' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Comprobante (screenshot)</label>
                    <input type="file" accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setScreenshot(e.target.files[0])} data-testid="screenshot-upload"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    {screenshot && <p className="text-xs text-emerald-600 mt-1">{screenshot.name} seleccionado</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ultimos 4 digitos de la referencia</label>
                    <input type="text" maxLength={4} value={lastDigits}
                      onChange={(e) => setLastDigits(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="Ej: 7890" data-testid="last-digits-input"
                      className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest font-mono text-lg focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setPaymentStep(1)} className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50">Atras</button>
                    <button onClick={uploadProof} disabled={uploading} data-testid="upload-proof-btn"
                      className="flex-1 bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-500 disabled:opacity-50">
                      {uploading ? 'Verificando...' : 'Enviar Comprobante'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pega aqui el SMS o email de tu banco</label>
                    <textarea value={smsText} onChange={(e) => setSmsText(e.target.value)}
                      data-testid="sms-text-input" rows={5} placeholder={'Ej: Banesco: Pago Movil enviado por Bs. 5.000,00\nRef: 1234567890 el 01/04/2026'}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono resize-none focus:ring-2 focus:ring-emerald-500" />
                    <p className="text-xs text-gray-400 mt-1">Copia y pega el texto exacto del SMS o email que recibiste de tu banco</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setPaymentStep(1)} className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50">Atras</button>
                    <button onClick={verifySms} disabled={uploading || !smsText} data-testid="verify-sms-btn"
                      className="flex-1 bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-500 disabled:opacity-50">
                      {uploading ? 'Verificando...' : 'Verificar por SMS'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {paymentStep === 3 && verificationResult && (
            <div className="p-6 space-y-4 text-center">
              <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${
                verificationResult.status === 'approved' ? 'bg-green-100' : 'bg-orange-100'
              }`}>
                {verificationResult.status === 'approved' ? (
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                )}
              </div>
              <h3 className="text-lg font-semibold" data-testid="verification-status">
                {verificationResult.status === 'approved' ? 'Pago Aprobado' : 'En Revision'}
              </h3>
              <p className="text-sm text-gray-600">{verificationResult.message}</p>
              {verificationResult.verification && (
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-left space-y-1">
                  <p>Confianza IA: <span className="font-medium">{verificationResult.verification.confidence}</span></p>
                  <p>Referencia: <span className={verificationResult.verification.digits_match ? 'text-green-600' : 'text-orange-600'}>{verificationResult.verification.digits_match ? 'Coincide' : 'No coincide'}</span></p>
                  <p>Monto: <span className={verificationResult.verification.amount_match ? 'text-green-600' : 'text-orange-600'}>{verificationResult.verification.amount_match ? 'Coincide' : 'No coincide'}</span></p>
                </div>
              )}
              <button onClick={resetPayment} data-testid="new-payment-btn"
                className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-500 mt-4">
                Nuevo Pago
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== Tab: Historial ===== */}
      {tab === 'historial' && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Mis Pagos</h3>
          {myPayments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No tienes pagos registrados</p>
          ) : (
            <div className="space-y-3">
              {myPayments.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg" data-testid={`payment-${p.id}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Bs. {p.amount_bs?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{p.payment_method?.replace('_', ' ')} - {p.purpose}</p>
                    <p className="text-xs text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleDateString('es-VE') : ''}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[p.status] || 'bg-gray-100'}`}>
                    {statusLabels[p.status] || p.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== Tab: Mis Datos Bancarios ===== */}
      {tab === 'mis-datos' && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Mis Datos Bancarios</h3>
          <p className="text-xs text-gray-500 mb-4">Estos datos se comparten solo cuando un comprador lo solicite y tu lo apruebes.</p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
              <select value={bankForm.bank_name} onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                data-testid="user-bank-name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar banco</option>
                {['Banesco', 'Mercantil', 'Provincial', 'BDV', 'Banco de Venezuela', 'BNC', 'Banco del Tesoro', 'Bancaribe', 'BOD', 'Exterior', 'Otro'].map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
              <input type="text" value={bankForm.phone} onChange={(e) => setBankForm({...bankForm, phone: e.target.value})}
                placeholder="04XX-XXXXXXX" data-testid="user-bank-phone"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cedula</label>
              <input type="text" value={bankForm.cedula} onChange={(e) => setBankForm({...bankForm, cedula: e.target.value})}
                placeholder="V-XXXXXXXX" data-testid="user-bank-cedula"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button onClick={saveBankData} data-testid="save-bank-data-btn"
              className="w-full bg-emerald-600 text-white py-2.5 rounded-lg font-medium hover:bg-emerald-500">
              Guardar Datos
            </button>
          </div>
        </div>
      )}

      {/* ===== Tab: Admin Review ===== */}
      {tab === 'admin-review' && isAdmin && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pagos Pendientes de Revision</h3>
          {pendingPayments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No hay pagos pendientes</p>
          ) : (
            <div className="space-y-4">
              {pendingPayments.map(p => (
                <div key={p.id} className="border border-orange-200 bg-orange-50 rounded-lg p-4" data-testid={`review-payment-${p.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">Bs. {p.amount_bs?.toLocaleString()}</p>
                      <p className="text-sm text-gray-600">{p.user_name} ({p.user_email})</p>
                      <p className="text-xs text-gray-500">{p.payment_method?.replace('_', ' ')} - {p.purpose}</p>
                      <p className="text-xs text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleString('es-VE') : ''}</p>
                    </div>
                    <div className="text-right">
                      {p.last_4_digits && <p className="text-sm font-mono bg-white px-2 py-1 rounded border">Ref: ...{p.last_4_digits}</p>}
                    </div>
                  </div>
                  {p.ocr_result && (
                    <div className="bg-white rounded-lg p-3 mb-3 text-xs space-y-1">
                      <p className="font-medium text-gray-700">Resultado OCR:</p>
                      <p>Monto leido: {p.ocr_result.monto || '?'} | Coincide: <span className={p.ocr_result.amount_match ? 'text-green-600' : 'text-red-600'}>{p.ocr_result.amount_match ? 'Si' : 'No'}</span></p>
                      <p>Referencia: {p.ocr_result.referencia || '?'} | Ultimos 4: {p.ocr_result.ultimos_4 || '?'} | Coincide: <span className={p.ocr_result.digits_match ? 'text-green-600' : 'text-red-600'}>{p.ocr_result.digits_match ? 'Si' : 'No'}</span></p>
                      <p>Banco: {p.ocr_result.banco_emisor || '?'} | Confianza: <span className="font-medium">{p.ocr_result.confianza || '?'}</span></p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => approvePayment(p.id)} data-testid={`approve-${p.id}`}
                      className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-500">
                      Aprobar
                    </button>
                    <button onClick={() => rejectPayment(p.id)} data-testid={`reject-${p.id}`}
                      className="flex-1 bg-red-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-400">
                      Rechazar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== Tab: Admin Config ===== */}
      {tab === 'admin-config' && isAdmin && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cuentas Bancarias de CUALAPP</h3>
          {bankAccounts.length > 0 && (
            <div className="space-y-2 mb-6">
              {bankAccounts.map(acc => (
                <div key={acc.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                  <div>
                    <p className="font-medium text-sm">{acc.bank_name} - {acc.account_holder}</p>
                    <p className="text-xs text-gray-500">Tel: {acc.phone} | CI: {acc.cedula} | {acc.account_type}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${acc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                    {acc.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
              ))}
            </div>
          )}
          <h4 className="font-medium text-gray-700 mb-3">Agregar Cuenta</h4>
          <div className="space-y-3">
            <select value={adminBank.bank_name} onChange={(e) => setAdminBank({...adminBank, bank_name: e.target.value})}
              data-testid="admin-bank-name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Seleccionar banco</option>
              {['Banesco', 'Mercantil', 'Provincial', 'BDV', 'Banco de Venezuela', 'BNC', 'Banco del Tesoro', 'Bancaribe', 'BOD', 'Exterior'].map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <input type="text" value={adminBank.account_holder}
              onChange={(e) => setAdminBank({...adminBank, account_holder: e.target.value})}
              placeholder="Titular de la cuenta" data-testid="admin-bank-holder"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <input type="text" value={adminBank.phone}
              onChange={(e) => setAdminBank({...adminBank, phone: e.target.value})}
              placeholder="Telefono (04XX-XXXXXXX)" data-testid="admin-bank-phone"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <input type="text" value={adminBank.cedula}
              onChange={(e) => setAdminBank({...adminBank, cedula: e.target.value})}
              placeholder="Cedula (V-XXXXXXXX)" data-testid="admin-bank-cedula"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <button onClick={saveAdminBank} data-testid="save-admin-bank-btn"
              className="w-full bg-purple-600 text-white py-2.5 rounded-lg font-medium hover:bg-purple-500">
              Agregar Cuenta
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagoMovilComponent;
